import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    LinearProgress,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import {
    DataGridPro,
    GridToolbarQuickFilter,
    itIT,
} from '@mui/x-data-grid-pro';
import unsplash from '../api/unsplash';
import { CustomFooterPersonalized } from '../utils/CustomFooterPersonalized';
import GlobalSnackbar from './Snackbar';

const SummaryBox = ({
    label,
    value,
    color = 'text.primary',
    active = false,
    onClick,
}) => (
    <Paper
        variant="outlined"
        onClick={onClick}
        sx={{
            p: 2,
            height: '100%',
            cursor: 'pointer',
            borderColor: active ? 'primary.main' : 'divider',
            bgcolor: active ? 'action.selected' : 'background.paper',
            '&:hover': {
                borderColor: 'primary.main',
            },
        }}
    >
        <Typography variant="body2" color="text.secondary">
            {label}
        </Typography>
        <Typography variant="h5" sx={{ color, mt: 0.5 }}>
            {value}
        </Typography>
    </Paper>
);

const Toolbar = () => (
    <Box sx={{ p: 1 }}>
        <GridToolbarQuickFilter placeholder="Cerca..." />
    </Box>
);

const DiagnosticaELearning = () => {
    const [rows, setRows] = useState([]);
    const [summary, setSummary] = useState({});
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [quickFilter, setQuickFilter] = useState('all');
    const [openSnack, setOpenSnack] = useState(false);
    const [snack, setSnack] = useState({
        severity: 'success',
        messaggio: '',
    });

    const headers = {
        Authorization: `Bearer ${localStorage.getItem('jwt')}`,
    };

    const handleCloseSnack = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnack(false);
    };

    const loadDiagnostica = async () => {
        setLoading(true);
        try {
            const response = await unsplash.get(
                'pigreco/diagnosticaELearning',
                {
                    headers,
                }
            );
            const data = response?.data?.data || [];
            setRows(
                data.map((row, index) => ({
                    id: row.Codice || index,
                    ...row,
                }))
            );
            setSummary(response?.data?.summary || {});
        } catch (error) {
            setSnack({
                severity: 'error',
                messaggio:
                    error.response?.data?.message ||
                    'Errore durante il caricamento della diagnostica e-learning',
            });
            setOpenSnack(true);
        } finally {
            setLoading(false);
        }
    };

    const syncCatalogo = async () => {
        setSyncing(true);
        try {
            const response = await unsplash.post(
                'pigreco/corsi',
                {},
                { headers }
            );
            const summary = response?.data?.summary || {};
            setSnack({
                severity: 'success',
                messaggio: `Catalogo Pigreco aggiornato${
                    summary.recuperati != null
                        ? `: ${summary.recuperati} corsi recuperati, ${summary.disattivati || 0} disattivati`
                        : ''
                }`,
            });
            setOpenSnack(true);
            await loadDiagnostica();
        } catch (error) {
            setSnack({
                severity: 'error',
                messaggio:
                    error.response?.data?.message ||
                    'Errore durante la sincronizzazione del catalogo Pigreco',
            });
            setOpenSnack(true);
        } finally {
            setSyncing(false);
        }
    };

    useEffect(() => {
        loadDiagnostica();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredRows = rows.filter((row) => {
        if (quickFilter === 'available') {
            return row.DisponibilePigreco;
        }
        if (quickFilter === 'unavailable') {
            return !row.DisponibilePigreco;
        }
        if (quickFilter === 'activeUnavailable') {
            return row.Attivo && !row.DisponibilePigreco;
        }
        if (quickFilter === 'missingEdition') {
            return Number(row.AuleELearningSenzaIdPigreco) > 0;
        }
        return true;
    });

    const handleQuickFilter = (filter) => {
        setQuickFilter((current) => (current === filter ? 'all' : filter));
    };

    const columns = [
        {
            field: 'Dettaglio',
            headerName: 'Aule',
            width: 110,
            headerAlign: 'center',
            align: 'center',
            sortable: false,
            renderCell: ({ row }) => (
                <Button
                    size="small"
                    startIcon={<VisibilityIcon />}
                    disabled={!row.Aule || row.Aule.length === 0}
                    onClick={() => setSelectedCourse(row)}
                >
                    Vedi
                </Button>
            ),
        },
        {
            field: 'DisponibilePigreco',
            headerName: 'Pigreco',
            width: 130,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ value }) => (
                <Chip
                    size="small"
                    color={value ? 'success' : 'error'}
                    label={value ? 'Disponibile' : 'Non disponibile'}
                />
            ),
        },
        {
            field: 'Attivo',
            headerName: 'Koala',
            width: 100,
            headerAlign: 'center',
            align: 'center',
            renderCell: ({ value }) => (
                <Chip
                    size="small"
                    color={value ? 'primary' : 'default'}
                    label={value ? 'Attivo' : 'Disattivo'}
                />
            ),
        },
        { field: 'Codice', headerName: 'Codice', width: 150 },
        {
            field: 'Descrizione',
            headerName: 'Descrizione',
            flex: 1,
            minWidth: 260,
        },
        {
            field: 'IDCorsoPiGreco',
            headerName: 'ID Pigreco',
            width: 120,
            type: 'number',
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'LavoratoriAgganciati',
            headerName: 'Lavoratori',
            width: 120,
            type: 'number',
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'AuleELearningAttive',
            headerName: 'Aule EL attive',
            width: 140,
            type: 'number',
            headerAlign: 'center',
            align: 'center',
        },
        {
            field: 'AuleELearningSenzaIdPigreco',
            headerName: 'Aule senza ID',
            width: 140,
            type: 'number',
            headerAlign: 'center',
            align: 'center',
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={2}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h5">Diagnostica e-learning</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Stato del catalogo corsi e-learning Koala rispetto al
                        catalogo Pigreco sincronizzato, con dettaglio delle aule
                        e dei partecipanti che le usano.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<RefreshIcon />}
                    onClick={syncCatalogo}
                    disabled={syncing || loading}
                >
                    Aggiorna catalogo
                </Button>
            </Stack>

            {(summary.attiviNonDisponibili > 0 ||
                summary.auleELearningSenzaIdPigreco > 0) && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Sono presenti corsi e-learning attivi non disponibili su
                    Pigreco o aule senza ID edizione Pigreco.
                </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} sm={6} md={2.4}>
                    <SummaryBox
                        label="Totale"
                        value={summary.totale || 0}
                        active={quickFilter === 'all'}
                        onClick={() => setQuickFilter('all')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <SummaryBox
                        label="Disponibili"
                        value={summary.disponibili || 0}
                        color="success.main"
                        active={quickFilter === 'available'}
                        onClick={() => handleQuickFilter('available')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <SummaryBox
                        label="Non disponibili"
                        value={summary.nonDisponibili || 0}
                        color="error.main"
                        active={quickFilter === 'unavailable'}
                        onClick={() => handleQuickFilter('unavailable')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <SummaryBox
                        label="Attivi non disponibili"
                        value={summary.attiviNonDisponibili || 0}
                        color="warning.main"
                        active={quickFilter === 'activeUnavailable'}
                        onClick={() => handleQuickFilter('activeUnavailable')}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={2.4}>
                    <SummaryBox
                        label="Aule senza ID"
                        value={summary.auleELearningSenzaIdPigreco || 0}
                        color="warning.main"
                        active={quickFilter === 'missingEdition'}
                        onClick={() => handleQuickFilter('missingEdition')}
                    />
                </Grid>
            </Grid>

            <Paper variant="outlined" sx={{ height: 'calc(100vh - 285px)' }}>
                <DataGridPro
                    rows={filteredRows}
                    columns={columns}
                    loading={loading || syncing}
                    localeText={
                        itIT.components.MuiDataGrid.defaultProps.localeText
                    }
                    disableSelectionOnClick
                    components={{
                        Toolbar,
                        Footer: CustomFooterPersonalized,
                        LoadingOverlay: LinearProgress,
                    }}
                    initialState={{
                        sorting: {
                            sortModel: [
                                { field: 'DisponibilePigreco', sort: 'asc' },
                                { field: 'Attivo', sort: 'desc' },
                            ],
                        },
                    }}
                />
            </Paper>

            <GlobalSnackbar
                severity={snack.severity}
                messaggio={snack.messaggio}
                openSnack={openSnack}
                handleCloseSnack={handleCloseSnack}
            />

            <Dialog
                open={Boolean(selectedCourse)}
                onClose={() => setSelectedCourse(null)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle>
                    Aule e partecipanti - {selectedCourse?.Codice}
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        {(selectedCourse?.Aule || []).map((aula) => (
                            <Paper
                                key={aula.CorsoHID}
                                variant="outlined"
                                sx={{ p: 2 }}
                            >
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    justifyContent="space-between"
                                    spacing={1}
                                    sx={{ mb: 1.5 }}
                                >
                                    <Box>
                                        <Typography variant="subtitle1">
                                            Aula #{aula.CorsoHID}
                                            {aula.NomeEdizione
                                                ? ` - ${aula.NomeEdizione}`
                                                : ''}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            ID edizione Pigreco:{' '}
                                            {aula.IdEdizionePigreco ||
                                                'assente'}
                                        </Typography>
                                    </Box>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                        useFlexGap
                                    >
                                        <Chip
                                            size="small"
                                            label={`${aula.PartecipantiCount} partecipanti`}
                                        />
                                        {aula.Aziende?.map((azienda) => (
                                            <Chip
                                                key={azienda}
                                                size="small"
                                                label={azienda}
                                                variant="outlined"
                                            />
                                        ))}
                                    </Stack>
                                </Stack>

                                {aula.Partecipanti?.length > 0 ? (
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Cognome</TableCell>
                                                <TableCell>Nome</TableCell>
                                                <TableCell>CF</TableCell>
                                                <TableCell>Azienda</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {aula.Partecipanti.map(
                                                (partecipante) => (
                                                    <TableRow
                                                        key={
                                                            partecipante.PresenzaID
                                                        }
                                                    >
                                                        <TableCell>
                                                            {
                                                                partecipante.Cognome
                                                            }
                                                        </TableCell>
                                                        <TableCell>
                                                            {partecipante.Nome}
                                                        </TableCell>
                                                        <TableCell>
                                                            {partecipante.CF}
                                                        </TableCell>
                                                        <TableCell>
                                                            {
                                                                partecipante.RagioneSociale
                                                            }
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            )}
                                        </TableBody>
                                    </Table>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        Nessun partecipante associato.
                                    </Typography>
                                )}
                            </Paper>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedCourse(null)}>
                        Chiudi
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DiagnosticaELearning;
